import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  WarningAmberRounded,
  MenuBookRounded,
  StorageRounded,
  RocketLaunchRounded,
} from "@mui/icons-material";

const CreateWithoutKnowledgeConfirmDialog = ({
  open,
  isDarkMode,
  isCreatingAgent,
  missingRulebook,
  missingCustomerData,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 600,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: isDarkMode
            ? "rgba(255,255,255,0.02)"
            : "rgba(0,0,0,0.02)",
        }}
      >
        Proceed Without Knowledge Setup?
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ textAlign: "center", py: 1.5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <WarningAmberRounded sx={{ color: "#f59e0b" }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                color: isDarkMode ? "#ffffff" : "#111827",
              }}
            >
              Agent context may be limited
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              fontFamily: '"Poppins", sans-serif',
              color: isDarkMode ? "#9ca3af" : "#4b5563",
              maxWidth: 520,
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Rulebook helps the agent follow business rules and guardrails.
            Customer Data (D3) provides customer-specific context for better
            personalization. You can still create the agent now and configure
            these later.
          </Typography>

          <Box sx={{ mt: 2.5, display: "grid", gap: 1.25 }}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: missingRulebook ? "warning.light" : "success.light",
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: isDarkMode
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.01)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MenuBookRounded
                  sx={{ color: missingRulebook ? "warning.main" : "success.main" }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Rules book (D1)
                </Typography>
              </Box>
              <Chip
                size="small"
                label={missingRulebook ? "Off" : "On"}
                color={missingRulebook ? "warning" : "success"}
                variant={missingRulebook ? "filled" : "outlined"}
              />
            </Box>

            <Box
              sx={{
                border: "1px solid",
                borderColor: missingCustomerData ? "warning.light" : "success.light",
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: isDarkMode
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.01)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <StorageRounded
                  sx={{
                    color: missingCustomerData ? "warning.main" : "success.main",
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Customer Data (D3)
                </Typography>
              </Box>
              <Chip
                size="small"
                label={missingCustomerData ? "Off" : "On"}
                color={missingCustomerData ? "warning" : "success"}
                variant={missingCustomerData ? "filled" : "outlined"}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isCreatingAgent}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          Go Back
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isCreatingAgent}
          startIcon={
            isCreatingAgent ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <RocketLaunchRounded />
            )
          }
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 700,
            minWidth: 190,
            backgroundColor: "#e36250",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#d14e3c",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(227, 98, 80, 0.5)",
              color: "rgba(255, 255, 255, 0.7)",
            },
          }}
        >
          {isCreatingAgent ? "Creating..." : "Proceed Anyway"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateWithoutKnowledgeConfirmDialog;
